import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../../core/auth/AuthContext";
import { isInAppBrowser } from "../../core/inAppBrowser";
import {
  PUSH_ERROR_CODES,
  checkPushSupport,
  getOrRegisterPushToken,
  requestPushPermission,
  resolveSafePushLink,
  sendTestPushNotification,
  onForegroundMessage,
  unregisterPushDevice,
} from "./pushNotifications.service";
import { usePanelNotice } from "../../components/panelNotice/hooks/usePanelNotice";

const PushOptInContext = createContext(null);

export function isIosDevice() {
  if (typeof navigator === "undefined") return false;
  const userAgent = navigator.userAgent || "";
  const reportsIosDevice = /iPad|iPhone|iPod/.test(userAgent);
  const reportsDesktopIpad =
    /Macintosh/.test(userAgent) && Number(navigator.maxTouchPoints) > 1;
  const isLegacyMicrosoftBrowser =
    typeof window !== "undefined" && Boolean(window.MSStream);
  return (reportsIosDevice || reportsDesktopIpad) && !isLegacyMicrosoftBrowser;
}

export function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator?.standalone === true
  );
}

function readBrowserPermission() {
  return typeof Notification === "undefined"
    ? "unsupported"
    : Notification.permission;
}

function statusFromEnvironment() {
  if (isInAppBrowser()) return "browser-required";
  if (isIosDevice() && !isStandalonePwa()) return "install-required";

  const permission = readBrowserPermission();
  if (permission === "unsupported") return "unsupported";
  if (permission === "denied") return "denied";
  return "idle";
}

export function getPushErrorMessage(errorCode) {
  switch (errorCode) {
    case PUSH_ERROR_CODES.SERVICE_WORKER_FAILED:
      return "Le module de notifications n’a pas pu démarrer. Recharge Karango puis réessaie.";
    case PUSH_ERROR_CODES.TOKEN_FAILED:
      return "Le téléphone n’a pas pu être enregistré auprès du service de notifications.";
    case PUSH_ERROR_CODES.SERVER_REGISTRATION_FAILED:
      return "Le téléphone est autorisé, mais Karango n’a pas pu terminer son enregistrement.";
    case PUSH_ERROR_CODES.PERMISSION_DENIED:
      return "Les notifications sont bloquées dans les réglages de ce navigateur.";
    case PUSH_ERROR_CODES.NO_ACTIVE_TOKEN:
      return "Aucun téléphone actif n’est enregistré. Réactive d’abord les notifications.";
    case PUSH_ERROR_CODES.TEST_FAILED:
      return "La notification de test n’a pas pu être envoyée.";
    default:
      return "L’activation a échoué. Vérifie ta connexion puis réessaie.";
  }
}

export function PushOptInProvider({ children }) {
  const { isAuthenticated, isAnonymous } = useAuth();
  const { showNotice } = usePanelNotice();
  const [state, setState] = useState(() => ({
    status: statusFromEnvironment(),
    errorCode: null,
  }));
  // Precalcule isSupported() (firebase/messaging) EN DEHORS du clic : c'est un
  // appel async, et l'evaluer a l'interieur du handler d'activation forcerait
  // un `await` avant Notification.requestPermission(), ce qui fait perdre le
  // geste utilisateur transitoire sur Safari/iOS (cf. N01 de l'audit
  // notifications). null = pas encore determine, true/false = connu.
  const [isPushApiSupported, setIsPushApiSupported] = useState(null);

  useEffect(() => {
    let cancelled = false;
    checkPushSupport().then((supported) => {
      if (!cancelled) setIsPushApiSupported(supported);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const syncExistingPermission = useCallback(async ({ quiet = false } = {}) => {
    if (isInAppBrowser()) {
      setState({ status: "browser-required", errorCode: null });
      return { success: false, status: "browser-required" };
    }

    if (isIosDevice() && !isStandalonePwa()) {
      setState({ status: "install-required", errorCode: null });
      return { success: false, status: "install-required" };
    }

    const supported = await checkPushSupport();
    if (!supported) {
      setState({ status: "unsupported", errorCode: PUSH_ERROR_CODES.UNSUPPORTED });
      return { success: false, status: "unsupported" };
    }

    const permission = readBrowserPermission();
    if (permission === "denied") {
      setState({ status: "denied", errorCode: PUSH_ERROR_CODES.PERMISSION_DENIED });
      return { success: false, status: "denied" };
    }
    if (permission !== "granted") {
      setState({ status: "idle", errorCode: null });
      return { success: false, status: "idle" };
    }

    if (!quiet) setState({ status: "syncing", errorCode: null });

    try {
      await getOrRegisterPushToken();
      setState({ status: "granted", errorCode: null });
      return { success: true, status: "granted" };
    } catch (error) {
      const errorCode = error?.code || PUSH_ERROR_CODES.TOKEN_FAILED;
      setState({ status: "error", errorCode });
      return { success: false, status: "error", errorCode };
    }
  }, []);

  // N01 : ce handler est invoque directement par le tap de l'utilisateur.
  // AUCUN await/fetch/promesse ne doit s'executer avant l'appel a
  // Notification.requestPermission() (via requestPushPermission()) plus bas,
  // sous peine de perdre l'activation utilisateur transitoire sur Safari/iOS
  // et de voir la demande systeme ne jamais s'afficher. Les verifications
  // ci-dessous (navigateur in-app, iOS non installe, API absente) sont toutes
  // synchrones ; le seul controle async (isSupported() de firebase/messaging)
  // a deja ete precalcule par l'effet ci-dessus et n'est ici que relu depuis
  // l'etat — jamais await.
  const activate = useCallback(() => {
    if (isInAppBrowser()) {
      setState({ status: "browser-required", errorCode: null });
      return Promise.resolve({ success: false, status: "browser-required" });
    }

    if (isIosDevice() && !isStandalonePwa()) {
      setState({ status: "install-required", errorCode: null });
      return Promise.resolve({ success: false, status: "install-required" });
    }

    if (typeof Notification === "undefined" || isPushApiSupported === false) {
      setState({ status: "unsupported", errorCode: PUSH_ERROR_CODES.UNSUPPORTED });
      return Promise.resolve({ success: false, status: "unsupported" });
    }

    setState({ status: "loading", errorCode: null });

    const permissionPromise =
      readBrowserPermission() === "granted"
        ? Promise.resolve("granted")
        : requestPushPermission();

    return permissionPromise
      .then((permission) => {
        if (permission !== "granted") {
          setState({ status: "idle", errorCode: null });
          return { success: false, status: "idle" };
        }

        return syncExistingPermission();
      })
      .catch((error) => {
        const errorCode = error?.code || PUSH_ERROR_CODES.TOKEN_FAILED;
        const status =
          errorCode === PUSH_ERROR_CODES.PERMISSION_DENIED ? "denied" : "error";
        setState({ status, errorCode });
        return { success: false, status, errorCode };
      });
  }, [isPushApiSupported, syncExistingPermission]);

  const deactivate = useCallback(async () => {
    setState({ status: "disabling", errorCode: null });
    await unregisterPushDevice();
    setState({ status: "idle", errorCode: null });
    return { success: true, status: "idle" };
  }, []);

  const sendTest = useCallback(async ({ onRequestStarted } = {}) => {
    setState({ status: "testing", errorCode: null });
    try {
      // Refreshing the registration first guarantees that the test targets
      // this browser, rather than every device attached to the account.
      const pushToken = await getOrRegisterPushToken();
      const result = await sendTestPushNotification(pushToken.id, {
        onRequestStarted,
      });
      setState({ status: "granted", errorCode: null });
      return { success: true, data: result };
    } catch (error) {
      const errorCode = error?.code || PUSH_ERROR_CODES.TEST_FAILED;
      setState({ status: "granted", errorCode });
      return { success: false, errorCode };
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isAnonymous) return undefined;

    return onForegroundMessage((payload) => {
      const title = payload?.notification?.title || payload?.data?.title;
      const message = payload?.notification?.body || payload?.data?.body;
      const safeLink = resolveSafePushLink(payload?.data?.link);
      if (!title && !message) return;

      showNotice({
        type: "success",
        title: title || "Notification Karango",
        message: message || "Une nouvelle notification vient d'arriver.",
        glyph: "🔔",
        actionLabel: safeLink ? "Ouvrir" : undefined,
        onAction: safeLink ? () => window.location.assign(safeLink) : undefined,
        autoDismissMs: 5000,
      });
    });
  }, [isAnonymous, isAuthenticated, showNotice]);

  useEffect(() => {
    if (!isAuthenticated || isAnonymous) {
      setState({ status: statusFromEnvironment(), errorCode: null });
      return undefined;
    }

    let cancelled = false;

    const refresh = async () => {
      if (cancelled) return;
      await syncExistingPermission();
    };

    refresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAnonymous, isAuthenticated, syncExistingPermission]);

  const value = useMemo(
    () => ({
      ...state,
      activate,
      deactivate,
      sendTest,
      refresh: syncExistingPermission,
      needsIosInstall: isIosDevice() && !isStandalonePwa(),
      isBusy: ["loading", "syncing", "disabling", "testing"].includes(
        state.status
      ),
      isPushApiSupported,
    }),
    [activate, deactivate, isPushApiSupported, sendTest, state, syncExistingPermission]
  );

  return (
    <PushOptInContext.Provider value={value}>
      {children}
    </PushOptInContext.Provider>
  );
}

export function usePushOptIn() {
  const context = useContext(PushOptInContext);
  if (!context) {
    throw new Error("usePushOptIn doit être utilisé dans PushOptInProvider.");
  }
  return context;
}
